import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  FormControlLabel,
  Switch,
  Rating,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import axios from '../../api/axios';
import ImageUploader from './ImageUploader';

const defaultFormData = {
  authorName: '',
  authorRole: '',
  text: '',
  rating: 5,
  photo: '',
  order: 0,
  isActive: true,
};

const ReviewsManager = () => {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(defaultFormData);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get('/reviews');
      setReviews(response.data);
    } catch (error) {
      console.error('Ошибка при загрузке отзывов:', error);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
    setFormData(defaultFormData);
  };

  const handleEdit = (review) => {
    setEditingId(review._id);
    setFormData({
      authorName: review.authorName || '',
      authorRole: review.authorRole || '',
      text: review.text || '',
      rating: review.rating || 5,
      photo: review.photo || '',
      order: review.order || 0,
      isActive: review.isActive !== false,
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить этот отзыв?')) {
      return;
    }

    try {
      await axios.delete(`/reviews/${id}`);
      fetchReviews();
    } catch (error) {
      console.error('Ошибка при удалении отзыва:', error);
      alert(error.response?.data?.message || 'Не удалось удалить отзыв');
    }
  };

  const handleSubmit = async () => {
    if (!formData.authorName.trim() || !formData.text.trim()) {
      alert('Заполните имя автора и текст отзыва');
      return;
    }

    try {
      if (editingId) {
        await axios.put(`/reviews/${editingId}`, formData);
      } else {
        await axios.post('/reviews', formData);
      }
      handleClose();
      fetchReviews();
    } catch (error) {
      console.error('Ошибка при сохранении отзыва:', error);
      alert(error.response?.data?.message || 'Не удалось сохранить отзыв');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Отзывы
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
          Добавить отзыв
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Автор</TableCell>
              <TableCell>Текст</TableCell>
              <TableCell>Оценка</TableCell>
              <TableCell>Порядок</TableCell>
              <TableCell>Активен</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reviews.map((review) => (
              <TableRow key={review._id}>
                <TableCell>
                  {review.authorName}
                  {review.authorRole && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {review.authorRole}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Typography variant="body2" noWrap>
                    {review.text}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Rating value={review.rating || 0} readOnly size="small" />
                </TableCell>
                <TableCell>{review.order}</TableCell>
                <TableCell>{review.isActive ? 'Да' : 'Нет'}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(review)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(review._id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Редактировать отзыв' : 'Добавить отзыв'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Имя автора"
            value={formData.authorName}
            onChange={(e) => setFormData({ ...formData, authorName: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Статус автора (например, «мама Миши, 5 лет»)"
            value={formData.authorRole}
            onChange={(e) => setFormData({ ...formData, authorRole: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Текст отзыва"
            value={formData.text}
            onChange={(e) => setFormData({ ...formData, text: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            rows={4}
            required
          />
          <Box sx={{ my: 2 }}>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              Оценка
            </Typography>
            <Rating
              value={formData.rating}
              onChange={(e, newValue) => setFormData({ ...formData, rating: newValue || 1 })}
            />
          </Box>
          <Box sx={{ my: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Фото автора (опционально)
            </Typography>
            <ImageUploader
              currentImage={formData.photo}
              onUploadSuccess={(url) => setFormData({ ...formData, photo: url })}
            />
          </Box>
          <TextField
            label="Порядок"
            type="number"
            value={formData.order}
            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value, 10) || 0 })}
            fullWidth
            margin="normal"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="Активен (показывать на сайте)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Отмена</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingId ? 'Обновить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewsManager;
